// src/components/booking/BookingModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  Calendar, 
  Users, 
  MapPin, 
  Clock, 
  CreditCard, 
  Building2,
  User,
  Mail,
  Phone,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Star,
  Wifi,
  Car,
  Coffee,
  Shield,
  Loader2,
  Copy,
  ExternalLink,
  Download
} from 'lucide-react';
import { bookingService } from '../../services/bookingService';

const STEPS = {
  SEARCH_VALIDATION: 'search_validation',
  PERSONAL_DETAILS: 'personal_details', 
  BOOKING_SUMMARY: 'booking_summary',
  PAYMENT_METHOD: 'payment_method',
  CONFIRMATION: 'confirmation'
};

const PAYMENT_METHODS = {
  ONLINE: 'online',
  EFT: 'eft'
};

export const BookingModal = ({ 
  isOpen, 
  onClose, 
  place, 
  searchCriteria,
  bookSettings,
  customStyling 
}) => {
  const [currentStep, setCurrentStep] = useState(STEPS.SEARCH_VALIDATION);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bookingData, setBookingData] = useState({
    // Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    
    // Booking Details
    checkIn: searchCriteria?.checkIn || '',
    checkOut: searchCriteria?.checkOut || '',
    adults: searchCriteria?.adults || 2,
    children: searchCriteria?.children || 0,
    rooms: searchCriteria?.rooms || 1,
    location: searchCriteria?.location || '',
    
    // Payment
    paymentMethod: PAYMENT_METHODS.ONLINE,
    acceptTerms: false,
    newsletterOptIn: false
  });
  
  const [bookingReference, setBookingReference] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);

  const getPrefillValue = useCallback((field, defaultValue = '') => {
    if (!searchCriteria) return defaultValue;
    
    // Handle date fields with validation
    if ((field === 'checkIn' || field === 'checkOut') && searchCriteria[field]) {
      const date = new Date(searchCriteria[field]);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    }
    
    // Handle numeric fields with validation
    if (['adults', 'children', 'rooms'].includes(field)) {
      const value = parseInt(searchCriteria[field]);
      if (!isNaN(value) && value > 0) {
        return value;
      }
      return defaultValue || (field === 'adults' ? 2 : field === 'rooms' ? 1 : 0);
    }
    
    // Handle location fields
    if (['location', 'province', 'city'].includes(field) && searchCriteria[field]) {
      return searchCriteria[field].toString().trim();
    }
    
    return searchCriteria[field] || defaultValue;
  }, [searchCriteria]);

  // Check if required search fields are filled
  const hasRequiredSearchFields = useCallback(() => {
    if (!searchCriteria) return false;
    
    const requiredFields = ['checkIn', 'checkOut', 'location'];
    const hasAllRequired = requiredFields.every(field => {
      const value = bookingData[field] || searchCriteria[field];
      return value && value.toString().trim().length > 0;
    });
    
    // Additional validation for dates
    if (hasAllRequired) {
      const checkIn = new Date(bookingData.checkIn || searchCriteria.checkIn);
      const checkOut = new Date(bookingData.checkOut || searchCriteria.checkOut);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return checkIn >= today && checkOut > checkIn;
    }
    
    return hasAllRequired;
  }, [searchCriteria, bookingData]);

  // Calculate total price
  useEffect(() => {
    if (place && bookingData.checkIn && bookingData.checkOut) {
      const checkIn = new Date(bookingData.checkIn);
      const checkOut = new Date(bookingData.checkOut);
      const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      
      let basePrice = place.price || 0;
      let total = basePrice * nights * bookingData.rooms;
      
      // Add pricing adjustments based on adults/children if configured
      if (place.pricingType === 'per_person') {
        total = basePrice * (bookingData.adults + bookingData.children * 0.5) * nights;
      }
      
      setTotalPrice(total);
    }
  }, [place, bookingData.checkIn, bookingData.checkOut, bookingData.rooms, bookingData.adults, bookingData.children]);

  // Initialize modal - Always start with search validation
  useEffect(() => {
    if (isOpen) {
      // Generate booking reference
      const ref = `BK${Date.now().toString().slice(-8).toUpperCase()}`;
      setBookingReference(ref);
      
      // Enhanced prefilling with validation
      setBookingData(prev => ({
        ...prev,
        checkIn: getPrefillValue('checkIn', ''),
        checkOut: getPrefillValue('checkOut', ''),
        adults: getPrefillValue('adults', 2),
        children: getPrefillValue('children', 0),
        rooms: getPrefillValue('rooms', 1),
        location: getPrefillValue('location', ''),
        province: getPrefillValue('province', ''),
        city: getPrefillValue('city', ''),
      }));
      
      // Determine starting step based on search completion
      setTimeout(() => {
        if (hasRequiredSearchFields()) {
          setCurrentStep(STEPS.PERSONAL_DETAILS);
        } else {
          setCurrentStep(STEPS.SEARCH_VALIDATION);
        }
      }, 100);
      
      // Clear any previous errors
      setErrors({});
    }
  }, [isOpen, searchCriteria, getPrefillValue, hasRequiredSearchFields]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setBookingData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-validation for certain fields
      if (field === 'checkIn' && updated.checkOut) {
        const checkIn = new Date(value);
        const checkOut = new Date(updated.checkOut);
        if (checkOut <= checkIn) {
          // Auto-adjust checkout to be one day after checkin
          const newCheckOut = new Date(checkIn);
          newCheckOut.setDate(checkIn.getDate() + 1);
          updated.checkOut = newCheckOut.toISOString().split('T')[0];
        }
      }
      
      return updated;
    });
    
    // Clear validation error
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case STEPS.SEARCH_VALIDATION:
        if (!bookingData.checkIn) newErrors.checkIn = 'Check-in date is required';
        if (!bookingData.checkOut) newErrors.checkOut = 'Check-out date is required';
        if (!bookingData.location) newErrors.location = 'Location is required';
        if (!bookingData.adults || bookingData.adults < 1) newErrors.adults = 'At least 1 adult is required';
        if (!bookingData.rooms || bookingData.rooms < 1) newErrors.rooms = 'At least 1 room is required';
        
        // Validate date logic
        if (bookingData.checkIn && bookingData.checkOut) {
          const checkIn = new Date(bookingData.checkIn);
          const checkOut = new Date(bookingData.checkOut);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (checkIn < today) {
            newErrors.checkIn = 'Check-in date cannot be in the past';
          }
          if (checkOut <= checkIn) {
            newErrors.checkOut = 'Check-out date must be after check-in date';
          }
        }
        break;
        
      case STEPS.PERSONAL_DETAILS:
        if (!bookingData.firstName?.trim()) newErrors.firstName = 'First name is required';
        if (!bookingData.lastName?.trim()) newErrors.lastName = 'Last name is required';
        if (!bookingData.email?.trim()) newErrors.email = 'Email is required';
        if (!bookingData.phone?.trim()) newErrors.phone = 'Phone number is required';
        
        // Email validation
        if (bookingData.email && !/\S+@\S+\.\S+/.test(bookingData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        break;
        
      case STEPS.PAYMENT_METHOD:
        if (!bookingData.acceptTerms) {
          newErrors.acceptTerms = 'You must accept the terms and conditions';
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNextStep = () => {
    if (!validateCurrentStep()) return;
    
    const steps = Object.values(STEPS);
    const currentIndex = steps.indexOf(currentStep);
    
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  // Handle previous step
  const handlePreviousStep = () => {
    const steps = Object.values(STEPS);
    const currentIndex = steps.indexOf(currentStep);
    
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  // Handle booking submission
  const handleSubmitBooking = async () => {
    if (!validateCurrentStep()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const bookingDetails = {
        // Reference and tracking
        bookingReference,
        
        // Guest details
        firstName: bookingData.firstName,
        lastName: bookingData.lastName,
        email: bookingData.email,
        phone: bookingData.phone,
        specialRequests: bookingData.specialRequests,
        
        // Booking details
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        adults: bookingData.adults,
        children: bookingData.children,
        rooms: bookingData.rooms,
        location: bookingData.location,
        
        // Place details
        placeId: place?.id,
        placeName: place?.title,
        placeType: place?.type,
        totalPrice,
        
        // Payment details
        paymentMethod: bookingData.paymentMethod,
        paymentStatus: bookingData.paymentMethod === PAYMENT_METHODS.ONLINE ? 
          'pending_payment' : 'pending_processing',
        
        // Preferences
        newsletterOptIn: bookingData.newsletterOptIn,
        
        // Metadata
        bookId: bookSettings?.bookId,
        status: 'confirmed',
        source: 'booking_modal'
      };

      // Save booking using new CRUD service
      const bookingId = await bookingService.createBooking(bookingDetails);
      
      // Send confirmation email if service is available
      try {
        await bookingService.sendBookingConfirmation(bookingDetails);
      } catch (emailError) {
        console.warn('Email confirmation failed:', emailError);
        // Don't fail the booking if email fails
      }
      
      setCurrentStep(STEPS.CONFIRMATION);
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      setErrors({ submit: 'Failed to submit booking. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Copy EFT details to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (!isOpen) return null;

  const primaryColor = customStyling?.primaryColor || '#10b981';
  const isFirstStep = currentStep === STEPS.SEARCH_VALIDATION;
  const isLastStep = currentStep === STEPS.CONFIRMATION;
  const showButtons = !isLastStep;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentStep === STEPS.SEARCH_VALIDATION ? 'Search Details' :
               currentStep === STEPS.PERSONAL_DETAILS ? 'Your Details' :
               currentStep === STEPS.BOOKING_SUMMARY ? 'Booking Summary' :
               currentStep === STEPS.PAYMENT_METHOD ? 'Payment Method' :
               'Booking Confirmed'}
            </h2>
            {place && (
              <p className="text-gray-600 mt-1">{place.title}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        {!isFirstStep && !isLastStep && (
          <div className="px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-center space-x-4">
              {Object.values(STEPS).slice(1, -1).map((step, index) => {
                const isActive = currentStep === step;
                const isCompleted = Object.values(STEPS).slice(1, -1).indexOf(currentStep) > index;
                
                return (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      isCompleted ? 'bg-emerald-500 text-white' :
                      isActive ? 'bg-blue-500 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                    </div>
                    {index < Object.values(STEPS).slice(1, -1).length - 1 && (
                      <div className={`w-12 h-1 mx-2 transition-colors ${
                        isCompleted ? 'bg-emerald-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Validation Step */}
          {currentStep === STEPS.SEARCH_VALIDATION && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Complete Your Search Details
                </h3>
                <p className="text-gray-600">
                  Please fill in all required information to proceed with your booking
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Check-in Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-in Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      value={bookingData.checkIn}
                      onChange={(e) => handleInputChange('checkIn', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.checkIn ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.checkIn && (
                    <p className="mt-1 text-sm text-red-500">{errors.checkIn}</p>
                  )}
                </div>

                {/* Check-out Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-out Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      value={bookingData.checkOut}
                      onChange={(e) => handleInputChange('checkOut', e.target.value)}
                      min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.checkOut ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.checkOut && (
                    <p className="mt-1 text-sm text-red-500">{errors.checkOut}</p>
                  )}
                </div>

                {/* Location */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={bookingData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Enter city or destination"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.location ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                  )}
                </div>

                {/* Adults */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adults *
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <select
                      value={bookingData.adults}
                      onChange={(e) => handleInputChange('adults', parseInt(e.target.value))}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.adults ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <option key={num} value={num}>{num} Adult{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  {errors.adults && (
                    <p className="mt-1 text-sm text-red-500">{errors.adults}</p>
                  )}
                </div>

                {/* Children */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Children
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <select
                      value={bookingData.children}
                      onChange={(e) => handleInputChange('children', parseInt(e.target.value))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[0, 1, 2, 3, 4, 5, 6].map(num => (
                        <option key={num} value={num}>{num} {num === 1 ? 'Child' : 'Children'}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Rooms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rooms *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <select
                      value={bookingData.rooms}
                      onChange={(e) => handleInputChange('rooms', parseInt(e.target.value))}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.rooms ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      {[1, 2, 3, 4, 5].map(num => (
                        <option key={num} value={num}>{num} Room{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  {errors.rooms && (
                    <p className="mt-1 text-sm text-red-500">{errors.rooms}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Personal Details Step */}
          {currentStep === STEPS.PERSONAL_DETAILS && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Your Contact Information
                </h3>
                <p className="text-gray-600">
                  We'll use this information to confirm your booking
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={bookingData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your first name"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={bookingData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your last name"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={bookingData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your email address"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      value={bookingData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your phone number"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* Special Requests */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requests (Optional)
                </label>
                <textarea
                  value={bookingData.specialRequests}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any special requests or notes for your booking..."
                />
              </div>
            </div>
          )}

          {/* Booking Summary Step */}
          {currentStep === STEPS.BOOKING_SUMMARY && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Review Your Booking
                </h3>
                <p className="text-gray-600">
                  Please review all details before proceeding to payment
                </p>
              </div>

              {/* Booking Details Card */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">Check-in</p>
                        <p className="text-sm text-gray-600">
                          {new Date(bookingData.checkIn).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">Check-out</p>
                        <p className="text-sm text-gray-600">
                          {new Date(bookingData.checkOut).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">Guests</p>
                        <p className="text-sm text-gray-600">
                          {bookingData.adults} Adults
                          {bookingData.children > 0 && `, ${bookingData.children} Children`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium">Rooms</p>
                        <p className="text-sm text-gray-600">{bookingData.rooms} Room{bookingData.rooms > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guest Information Card */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Guest Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium">{bookingData.firstName} {bookingData.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{bookingData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium">{bookingData.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium">{bookingData.location}</p>
                  </div>
                </div>
                
                {bookingData.specialRequests && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600">Special Requests</p>
                    <p className="font-medium">{bookingData.specialRequests}</p>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Price Breakdown</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Base Rate × {Math.ceil((new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) / (1000 * 60 * 60 * 24))} nights
                    </span>
                    <span className="font-medium">R{(place?.price || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rooms × {bookingData.rooms}</span>
                    <span className="font-medium">+R{((place?.price || 0) * (bookingData.rooms - 1)).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-emerald-200 pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span>R{totalPrice.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Step */}
          {currentStep === STEPS.PAYMENT_METHOD && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Choose Payment Method
                </h3>
                <p className="text-gray-600">
                  Select how you'd like to complete your booking
                </p>
              </div>

              {/* Payment Methods */}
              <div className="space-y-4">
                {/* Online Payment */}
                <div 
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-colors ${
                    bookingData.paymentMethod === PAYMENT_METHODS.ONLINE 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('paymentMethod', PAYMENT_METHODS.ONLINE)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        bookingData.paymentMethod === PAYMENT_METHODS.ONLINE 
                          ? 'border-blue-500 bg-blue-500' 
                          : 'border-gray-300'
                      }`}>
                        {bookingData.paymentMethod === PAYMENT_METHODS.ONLINE && (
                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Pay Online</h4>
                        <p className="text-sm text-gray-600">Credit card, debit card, or digital wallet</p>
                      </div>
                    </div>
                    <CreditCard className="h-6 w-6 text-gray-400" />
                  </div>
                </div>

                {/* EFT Payment */}
                <div 
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-colors ${
                    bookingData.paymentMethod === PAYMENT_METHODS.EFT 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('paymentMethod', PAYMENT_METHODS.EFT)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        bookingData.paymentMethod === PAYMENT_METHODS.EFT 
                          ? 'border-blue-500 bg-blue-500' 
                          : 'border-gray-300'
                      }`}>
                        {bookingData.paymentMethod === PAYMENT_METHODS.EFT && (
                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Bank Transfer (EFT)</h4>
                        <p className="text-sm text-gray-600">Transfer directly to our bank account</p>
                      </div>
                    </div>
                    <ExternalLink className="h-6 w-6 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* EFT Details */}
              {bookingData.paymentMethod === PAYMENT_METHODS.EFT && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-900 mb-4">Bank Transfer Details</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">Account Name:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-blue-900">Your Business Name</span>
                        <button 
                          onClick={() => copyToClipboard('Your Business Name')}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">Bank:</span>
                      <span className="font-medium text-blue-900">Standard Bank</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-blue-900">123456789</span>
                        <button 
                          onClick={() => copyToClipboard('123456789')}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">Branch Code:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-blue-900">051001</span>
                        <button 
                          onClick={() => copyToClipboard('051001')}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">Reference:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-blue-900">{bookingReference}</span>
                        <button 
                          onClick={() => copyToClipboard(bookingReference)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">Amount:</span>
                      <span className="font-bold text-blue-900">R{totalPrice.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                    <p className="text-xs text-blue-800">
                      Please use the booking reference <strong>{bookingReference}</strong> when making your transfer. 
                      Your booking will be confirmed once payment is received.
                    </p>
                  </div>
                </div>
              )}

              {/* Terms and Conditions */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={bookingData.acceptTerms}
                    onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                    I accept the <a href="#" className="text-blue-600 hover:underline">Terms and Conditions</a> and 
                    <a href="#" className="text-blue-600 hover:underline ml-1">Privacy Policy</a> *
                  </label>
                </div>
                {errors.acceptTerms && (
                  <p className="text-sm text-red-500">{errors.acceptTerms}</p>
                )}

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="newsletterOptIn"
                    checked={bookingData.newsletterOptIn}
                    onChange={(e) => handleInputChange('newsletterOptIn', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="newsletterOptIn" className="text-sm text-gray-700">
                    Subscribe to our newsletter for special offers and updates
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Step */}
          {currentStep === STEPS.CONFIRMATION && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Booking Confirmed!
                </h3>
                <p className="text-gray-600 text-lg">
                  Your booking reference is <strong>{bookingReference}</strong>
                </p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <h4 className="font-semibold text-emerald-900 mb-4">What happens next?</h4>
                <div className="space-y-3 text-sm text-emerald-800">
                  {bookingData.paymentMethod === PAYMENT_METHODS.ONLINE ? (
                    <>
                      <p>✓ Payment has been processed successfully</p>
                      <p>✓ Booking confirmation sent to {bookingData.email}</p>
                      <p>✓ Property will contact you within 24 hours</p>
                    </>
                  ) : (
                    <>
                      <p>✓ Booking details sent to {bookingData.email}</p>
                      <p>✓ Please complete bank transfer using the provided details</p>
                      <p>✓ Booking will be confirmed once payment is received</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    // Download booking details logic
                    console.log('Download booking details');
                  }}
                  className="flex items-center justify-center gap-2 px-6 py-3 text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download Details
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: primaryColor }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Buttons - Fixed at bottom */}
        {showButtons && (
          <div className="border-t border-gray-200 p-6 bg-white">
            <div className="flex items-center justify-between">
              <button
                onClick={handlePreviousStep}
                disabled={isFirstStep}
                className={`flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg transition-colors ${
                  isFirstStep
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>

              <div className="flex items-center gap-3">
                {errors.submit && (
                  <p className="text-red-500 text-sm">{errors.submit}</p>
                )}
                
                {currentStep === STEPS.PAYMENT_METHOD ? (
                  <button
                    onClick={handleSubmitBooking}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-8 py-3 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : bookingData.paymentMethod === PAYMENT_METHODS.ONLINE ? (
                      <CreditCard className="h-4 w-4" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    {isLoading ? 'Processing...' : 
                     bookingData.paymentMethod === PAYMENT_METHODS.ONLINE ? 'Pay Now' : 'Confirm Booking'}
                  </button>
                ) : (
                  <button
                    onClick={handleNextStep}
                    className="flex items-center gap-2 px-8 py-3 text-white rounded-lg transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;